import React, { createContext, useContext } from 'react';

import type { RteUiAdapter } from './types';

/**
 * UI Adapter Context
 *
 * The headless core exposes only logic (editor state, extensions, commands).
 * A UI adapter supplies concrete visual components (Root, Toolbar, ControlGroup,
 * ControlButton, Content, optional LoadingOverlay, Icons, etc.).
 *
 * This context allows any headless-aware composite (e.g., HeadlessRichTextEditor
 * or future higher-level wrappers) to retrieve the active adapter at runtime.
 *
 * Adapters MUST wrap editor usage with <RteUiProvider adapter={...}>.
 */

const RteUiContext = createContext<RteUiAdapter | null>(null);

export interface RteUiProviderProps {
  adapter: RteUiAdapter;
  children: React.ReactNode;
}

/**
 * Provides the currently configured UI adapter implementation.
 */
export function RteUiProvider(props: Readonly<RteUiProviderProps>) {
  const { adapter, children } = props;
  return <RteUiContext.Provider value={adapter}>{children}</RteUiContext.Provider>;
}

/**
 * Access the active UI adapter.
 * Throws if no provider is present to surface integration mistakes early.
 */
export function useRteUi(): RteUiAdapter {
  const ctx = useContext(RteUiContext);
  if (ctx === null) {
    throw new Error(
      'useRteUi must be used within a <RteUiProvider>. ' +
        'Wrap your editor tree with an adapter provider.',
    );
  }
  return ctx;
}

/**
 * Utility guard for non-hook contexts (e.g., inside class components or plain functions).
 */
export function assertRteUiAdapter(adapter: RteUiAdapter | null): asserts adapter is RteUiAdapter {
  if (!adapter) {
    throw new Error('Rich Text Editor UI adapter not found in context.');
  }
}
